<template>
    <div id="theme-details" class="tw-bg-gray-100 tw-flex tw-flex-col">
        <div class="header">
            <div class="breadcrumbs" id="breadcrumbs">
                <router-link class="breadcrumb-link" to="/surveys/dashboard">Bookblock HUB Surveys</router-link> &gt;
                <router-link v-if="$route.name === 'surveys-theme-details'" class="breadcrumb-link" to="/surveys/themes">Your Survey Themes  &gt;</router-link>
                <router-link v-if="$route.name === 'surveys-themes-gallery-details'" class="breadcrumb-link" to="/surveys/themes/gallery">Bookblock HUB Theme Gallery  &gt;</router-link>
                <router-link v-if="$route.name === 'surveys-theme-details'" class="breadcrumb-link" :to="'/surveys/themes/' + theme.id">{{ theme.name }}</router-link>
                <router-link v-if="$route.name === 'surveys-themes-gallery-details'" class="breadcrumb-link" :to="'/surveys/themes/gallery/' + theme.id">{{ theme.name }}</router-link>
            </div>
            <h3>{{ theme.name }}</h3>
        </div>
        <div class="theme-detail tw-flex tw-bg-white tw-h-full tw-p-4 tw-flex-grow questions">
            <div class="left-bar tw-flex tw-flex-col">
                <h5 class="tw-text-base"><b>Theme Style</b></h5>
                <form class="large-form seamless theme-style-form tw-overflow-auto">
                    <div class="form-line">
                        <div class="form-field">
                            <label>Questions & Titles</label>
                            <div class="form-row">
                                <WSFontPicker 
                                    class="form-field"
                                    :defaultFont="styles.question_font"
                                    @update="styles.question_font = $event"
                                    @change="onSaveStyles(false)"
                                />
                            </div>
                            <div class="form-row">
                                <div>
                                    <WSSelect 
                                        :options="sizes"
                                        :default="styles.question_size"
                                        @change="onSaveStyles(true)"
                                        @input="styles.question_size = $event"
                                    />
                                </div>
                                <div>
                                    <WSColorPicker 
                                        :default-color="styles.question_color" 
                                        @update="styles.question_color = $event"
                                        @close="onSaveStyles(true)"
                                        :swatches="swatches"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="form-line">
                        <div class="form-field">
                            <label>Answers</label>
                            <div class="form-row">
                                <WSFontPicker 
                                    class="form-field"
                                    :defaultFont="styles.answer_font"
                                    @update="styles.answer_font = $event"
                                    @change="onSaveStyles(false)"
                                />
                            </div>
                            <div class="form-row">
                                <div>
                                    <WSSelect 
                                        :options="sizes"
                                        :default="styles.answer_size"
                                        @change="onSaveStyles(true)"
                                        @input="styles.answer_size = $event"
                                    />
                                </div>
                                <div>
                                    <WSColorPicker 
                                        :default-color="styles.answer_color" 
                                        @update="styles.answer_color = $event"
                                        @close="onSaveStyles(true)"
                                        :swatches="swatches"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="form-line">
                        <div class="form-field">
                            <label>Button Style</label>
                            <div class="form-row">
                                <WSFontPicker 
                                    class="form-field"
                                    :defaultFont="styles.button_font"
                                    @update="styles.button_font = $event"
                                    @change="onSaveStyles(false)"
                                />
                            </div>
                            <div class="form-row">
                                <div>
                                    <label>Background</label>
                                    <WSColorPicker 
                                        :default-color="styles.button_bg_color" 
                                        @update="styles.button_bg_color = $event"
                                        @close="onSaveStyles(true)"
                                        :swatches="swatches"
                                    />
                                </div>
                                <div>
                                    <label>Text</label>
                                    <WSColorPicker 
                                        :default-color="styles.button_color" 
                                        @update="styles.button_color = $event"
                                        @close="onSaveStyles(true)"
                                        :swatches="swatches"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="form-line">
                        <div class="form-field">
                            <label>Page Background</label>
                            <div class="form-row" >
                                <div>
                                    <WSColorPicker 
                                        :default-color="styles.page_bg_color" 
                                        @update="styles.page_bg_color = $event"
                                        @close="onSaveStyles(true)"
                                        :swatches="swatches"
                                        style="flex-basis: 100%;"
                                        class="top"
                                    />
                                </div>
                            </div>
                            <div class="form-row">
                                <media-gallery-selector 
                                    v-model="styles.page_bg_image" 
                                    @input="onSaveStyles(true)" 
                                    class="form-field media-gallery-selector"
                                />
                            </div>
                        </div>
                    </div>
                </form>
                <div class="tw-mt-auto tw-text-center">
                    <div class="ws-button ws-button-main add-new tw-w-full mt-3"
                        @click="showSettings = true">Theme Settings
                    </div>
                </div>
            </div>
            <div class="center-bar tw-rounded-lg" ref="pageRef">
                <div class="inner tw-h-full">
                    <page-view v-if="selectedPage" :page="selectedPage" :styles="styles" readonly view="theme-preview" :editable="false" />
                </div>
            </div>
            <div class="right-bar">
                <h5 class="tw-text-base"><b>Theme Preview</b></h5>
                <div id="theme-preview-list" class="tw-overflow-auto">
                    <div v-for="page in pages"
                         :key="page.name"
                         class="page"
                         :class="{ 'active':  page.name === selectedPage?.name, [getThemePageClass(page)]: true }"
                         @click="onSelect(page)">
                            {{ page.name }}
                    </div>
                </div>
            </div>
        </div>

        <modal v-if="showSettings" @close="showSettings = false">
            <survey-theme-form :gallery-mode="!theme.team_id" :theme="theme" @save="onUpdateThemeSettings"
                               @cancel="showSettings = false"/>
        </modal>
    </div>
</template>

<script>
import html2canvas from "html2canvas";
import PageView from "@/pages/surveys/PageView";
import { debounce } from "lodash-es";
import Modal from "@/components/modals/Modal";
import SurveyThemeForm from "@/pages/surveys/SurveyThemeForm";
import MediaGallerySelector from "@/components/MediaGallerySelector";
import WSSelect from "@/components/elements/WSSelect";
import WSColorPicker from "@/components/elements/WSColorPicker";
import WSFontPicker from "@/components/elements/WSFontPicker";

export default {
    name: 'SurveyThemeDetails',
    components: { MediaGallerySelector, SurveyThemeForm, Modal, PageView, WSSelect, WSColorPicker, WSFontPicker },
    data() {
        return {
            themeStore: this.$route.name === 'surveys-themes-gallery-details' ? 'surveyThemesGallery' : 'surveyThemes',
            themeListRoute: this.$route.name === 'surveys-themes-gallery-details' ? 'surveys-themes-gallery' : 'surveys-themes',
            activeOnly: undefined,
            showSettings: false,
            selectedPage: undefined,
            styles: {
                question_font: 'Lato',
                question_size: '24px',
                question_color: '#333333',
                answer_font: 'Lato',
                answer_size: '24px',
                answer_color: '#2f65b4',
                button_font: 'Lato',
                button_bg_color: '#2f65b4',
                button_color: '#FFFFFF',
                page_bg_color: '#FFFFFF',
                page_bg_image: ''
            },
            sizes: [
                { value: '24px', text: 'Small' },
                { value: '30px', text: 'Medium' },
                { value: '36px', text: 'Large' },
            ],
            swatches: [
                '#222222',
                '#27376c',
                '#2f65b4',
                '#3b82be'
            ],
            pages: [
                {
                    name: 'Welcome Page',
                    type: 'context',
                    title: 'This is your Welcome Page title',
                    subtitle: 'This is your Welcome Page description',
                    button_text: 'Start'
                },
                {
                    name: 'Short text',
                    type: 'question',
                    questions: [
                        {
                            id: 1,
                            name: 'A question with a simple short answer.',
                            description: 'Just one or several words, really.',
                            type: 'short-text',
                            default: 'This is a sample answer'
                        }
                    ]
                },
                {
                    name: 'Email',
                    type: 'question',
                    questions: [
                        {
                            id: 2,
                            name: 'What is your email address?',
                            description: 'Where can we reach you.',
                            type: 'email',
                            default: 'hub@bookblock.com'
                        }
                    ]
                },
                {
                    name: 'Phone number',
                    type: 'question',
                    questions: [
                        {
                            id: 3,
                            name: 'What is your phone number?',
                            description: 'For us to reach you if needed.',
                            type: 'phone-number',
                            default: '123-456-7890'
                        }
                    ]
                },
                {
                    name: 'Date',
                    type: 'question',
                    questions: [
                        {
                            id: 4,
                            name: 'When did you visit our shop?',
                            description: 'Please choose the date below.',
                            type: 'date',
                            default: '2023-01-01'
                        }
                    ]
                },
                {
                    name: 'Website',
                    type: 'question',
                    questions: [
                        {
                            id: 5,
                            name: 'What is your company website?',
                            description: 'Please give a link to your website below.',
                            type: 'website',
                            default: 'hub.bookblock.com',
                        }
                    ]
                },
                {
                    name: 'Number',
                    type: 'question',
                    questions: [
                        {
                            id: 6,
                            name: 'What is your favourite number?',
                            description: 'No limits.',
                            type: 'number',
                            default: 42
                        }
                    ]
                },
                {
                    name: 'Long Text',
                    type: 'question',
                    questions: [
                        {
                            id: 7,
                            name: 'Please give us a long answer to our question.',
                            description: 'Don\'t hold back.',
                            type: 'long-text',
                            default: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla in orci est. Suspendisse fringilla facilisis blandit. Sed eget sapien mi. Phasellus arcu justo, congue eu justo ut, consequat ornare nisi. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae; Pellentesque vitae porta leo. Duis id augue sodales, vulputate nibh id, tincidunt elit. Aliquam sit amet neque sit amet odio bibendum eleifend at eu diam. Aenean vehicula ante turpis, vitae venenatis nunc varius et. Nullam tincidunt mi in lorem placerat, eu interdum nibh cursus. Nulla facilisi. Duis molestie, augue sit amet rutrum aliquam, urna odio mattis tortor, in tincidunt nisi tortor non orci. Fusce laoreet sagittis molestie. Sed sed arcu nulla. Phasellus molestie lorem elit, sit amet molestie dui varius ut.\n' +
                                    'Nullam nec varius purus. Vestibulum lacinia suscipit magna at placerat. Integer sagittis tristique mi, et mollis erat eleifend imperdiet. Mauris ut metus in est eleifend maximus et sed erat. Etiam sit amet gravida quam, sit amet mattis nibh. Maecenas sodales venenatis risus quis auctor. Mauris et tortor tellus. Aenean accumsan odio in hendrerit fermentum. Ut malesuada lorem libero, eget pulvinar ligula posuere ut. Quisque iaculis nisl ante, quis aliquam eros gravida ultricies.'
                        }
                    ]
                },
                {
                    name: 'Single choice',
                    type: 'question',
                    questions: [
                        {
                            id: 8,
                            name: 'What is your favourite letter from A to E?',
                            description: 'Please choose one from the options below.',
                            type: 'single-choice',
                            default: 'Option A',
                            options: [
                                'Option A',
                                'Option B',
                                'Option C',
                            ]
                        }
                    ]
                },
                {
                    name: 'Multiple choice',
                    type: 'question',
                    questions: [
                        {
                            id: 9,
                            name: 'Choose all your favourite letters from A to E',
                            description: 'Please choose all that apply.',
                            type: 'multiple-choice',
                            default: ['Option A'],
                            options: [
                                'Option A',
                                'Option B',
                                'Option C',
                            ],
                        }
                    ]
                },
                {
                    name: 'Opinion scale',
                    type: 'question',
                    questions: [
                        {
                            id: 10,
                            name: 'How do you rate this survey?',
                            description: 'Please set your rating below.',
                            type: 'opinion-scale',
                            default: 5,
                            max_value: 10,
                            start_from_zero: 1,
                            min_label: 'Horrible',
                            max_label: 'Awesome',
                        }
                    ]
                },
                {
                    name: 'Rating',
                    type: 'question',
                    questions: [
                        {
                            id: 11,
                            name: 'Please rate our service.',
                            description: 'You can leave your rating below.',
                            type: 'rating',
                            default: 3,
                            max_value: 5,
                        }
                    ]
                },
                {
                    name: 'Ranking',
                    type: 'question',
                    questions: [
                        {
                            id: 12,
                            name: 'Please order letters by preference.',
                            description: 'The most favourite first, the least favourite last.',
                            type: 'ranking',
                            options: [
                                'Option A',
                                'Option B',
                                'Option C',
                            ]
                        }
                    ]
                },
                {
                    name: 'Boolean',
                    type: 'question',
                    questions: [
                        {
                            id: 13,
                            name: 'Yes or No?',
                            description: 'That is indeed the real question.',
                            type: 'boolean'
                        }
                    ]
                },
            ]
        }
    },
    computed: {
        theme() {
            return this.$store.getters[`${this.themeStore}/selected`] || {};
        },
        coreLoaded() {
            return this.$store.state.coreLoaded;
        },
    },
    beforeRouteUpdate(to) {
        this.loadTheme(to.params.theme_id);
    },
    created() {
        this.selectedPage = this.pages[0]
        this.loadTheme(this.$route.params.theme_id);
        this.onSaveStyles = debounce(this.saveStyles, 1000);
    },
    mounted() {
        
    },
    methods: {
        async loadTheme(id) {
            try {
                this.$store.commit(`${this.themeStore}/setSelected`, id)
                if (this.theme.styles) this.styles = {...this.theme.styles}
                await this.$store.dispatch(`${this.themeStore}/getById`, id)
                if (this.theme.styles) this.styles = {...this.theme.styles}
            } catch (e) {
                if (e.response?.status === 404) {
                    this.$store.commit(`${this.themeStore}/setSelected`, undefined)
                    this.$router.push({name: this.themeListRoute})
                }
            }
        },
        onSelect(page) {
            this.selectedPage = page;
            if (page.questions) {
                this.$store.commit('surveyQuestions/setList', page.questions)
            }
        },
        async saveStyles() {
            if (!this.theme?.id) return;
            let payload = {...this.theme, styles: this.styles};
            await this.$store.dispatch(`${this.themeStore}/update`, payload);
        },
        async onUpdateThemeSettings(payload) {
            await this.$store.dispatch(`${this.themeStore}/update`, payload);
            this.showSettings = false;
        },
        async onActivate(theme) {
            await this.$store.dispatch(`${this.themeStore}/publish`, theme);
        },
        async onDeactivate(theme) {
            await this.$store.dispatch(`${this.themeStore}/unpublish`, theme);
        },
        getThemePageClass(page) {
            return (page.questions) ? page.questions[0].type : page.name.toLowerCase().replace(' ', '-');
        }
    },
}
</script>
